// Base import
import Alpine, { Alpine as AlpineInterface } from 'alpinejs';
// Components import
import initOfferDrawerComponent from './components/offerDrawer';
import initChoiceComponent from './components/choice';
import initQuantityComponent from './components/quantity';
import initFieldsComponent from './components/fields';
import initMeetHoursComponent from './components/meethours';
import { formatPhoneForCrm, getPhonePrefixStore } from './components/phonePrefixComponent';

// Other import
import { trybaPhoneMask } from '../helpers/validation';
import { ApiError } from '../helpers/api';
import {
    JsonFormType,
} from "./types";
import { ApiSuccess, send } from '../helpers/api';
import { AvailibilitySlot } from '../../components/booking/types';
import { sendEventDataLayer, sendTunnelForm } from '../alpine/include/taggage'
import { getUserData, setUserDataLocalisation, setUserDataShop } from '../../helpers/user'
import { addQwamplifyToFormData, addUtmToFormData, autocompleteFromFieldName } from '../helpers/form'

/**
 * Import de données (states, fonctions, etc...) de composants enfant :
 * Lors de l'importation de composant enfant, veuillez consérver une nomenclature tel que :
 * '.....State' pour l'import de states
 * '.....Fct' pour l'import de fonctions.
 *
 * Modale d'abandon (Code préparé, pour l'instant réalisée via abTasty)
 * @property {function} showExitModalFct Fonction qui gère l'affichage de la modale d'abandon.
 * @property {boolean} showExitModalState State dont dépend le composant twig pour l'affichage ou non de la modale d'abandon.
 * Drawer offre
 * @property {function} showOfferDrawerFct Fonction qui gère l'affichage du drawer avec les informations sur l'offre en cours.
 * @property {boolean} showOfferDrawerState State dont dépend le composant twig pour l'affichage ou non du drawer avec les informations sur l'offre en cours.
 * Form
 * @property {array} formSteps Étapes du formulaire
 * @property {array} initStepKey Clé de l'étape d'initialisation du formulaire
 * @property {object} step Objet qui contient étape actuelle
 * @property {string} stepTitle Titre de l'étape actuelle
 * @property {array} dataToSend tableau des données à envoyer
 * Choice
 * @property {function} handleChoiceFct Permet de gérer la sélection d'un ou plusieurs items, initialement à false
 * @property {boolean} isMultipleChoiceState Permet de gérer le cas d'un choix unique ou multiple
 * @property {array<object>} choicesState Tableau d'objets dans lequel figurera un ou plusieurs choix. La clé de chaque object correspond à la data "tag" du state "step".
 * Quantity
 * @property {function} handleQuantityActiveStateFct Permet d'activer ou non un accordeon et d'envoyer la valeur renseignée
 * @property {function} getQuantityStateFct Permet d'avoir la quantité de la taille souhaitée
 * @property {function} handleQuantityFct Permet d'augmenter et diminuer le nombre choisi en fonction de la taille
 * @property {boolean} quantityActiveState Etat général si au moins un des accordeon est sélectionné afin de permettre le passage à l'étape suivante
 * @property {boolean} isSmallActiveState Etat pour la quantité petite (coché ou non)
 * @property {number} smallQuantityState Nombre choisi pour la taille petite
 * @property {boolean} isMediumActiveState Etat pour la quantité moyenne (coché ou non)
 * @property {number} mediumQuantityState Nombre choisi pour la taille moyenne
 * @property {boolean} isLargeActiveState Etat pour la quantité grande (coché ou non)
 * @property {number} largeQuantityState Nombre choisi pour la taille grande
 * Fields
 * @property {function} initInputFileFct Permet l'initialisation de champ input file (ainsi que le retour des messages de validation/erreur)
 * @property {function} validateLastStepFct Permet la validation de la dernière étape (affichage des erreurs etc..)
 * @property {number} maxCharsLastNameState Nombre de caractères max pour le champs lastName
 * @property {number} maxCharsFirstNameState Nombre de caractères max pour le champs firstName
 * @property {number} maxCharsEmailState Nombre de caractères max pour le champs email
 * @property {number} maxCharsAddressState Nombre de caractères max pour le champs adresse
 * @property {number} maxCharsTextareaState Nombre de caractères max pour le champs textarea
 * @property {number} nbOfCharsState Nombre de caractères
 * @property {function} selectedElement Permet de sauvegarder le choix de la ville / code postal du client dans le local storage
*/

import { City, storeLocatorAutocomplete } from "../helpers/storeLocator";
import { AlpineComponentTryba } from '../alpine/include/global';


enum FormType {
    QUOTATION = "/api/forms/quotation-2/",
    APPOINTMENT = "/api/forms/appointment-2/",
    QUOTATION_V3 = "/api/forms/quotation-3/",
    APPOINTMENT_V3 = "/api/forms/appointment-3/"
  }


type AlpineTryba = Omit<AlpineInterface, 'data'> & {
    data(name: string, callback: (jsonForm: JsonFormType, myFormName: string, abTestType: any) => AlpineComponentTryba): void;
}

window.AlpineTryba = Alpine as unknown as AlpineTryba;

document.addEventListener('alpine:init', () => {
    window.AlpineTryba.data('initTunnels', (form: JsonFormType) => {
        return {
            // State and function for offer drawer
            showOfferDrawerFct: initOfferDrawerComponent.showOfferDrawerFct,
            showOfferDrawerState: initOfferDrawerComponent.showOfferDrawerState,
            // Form
            formType: null,
            zipFromStorageValue: '',
            formSteps: form.steps,
            initStepKey: form.initStep,
            step: null,
            stepTitle: null,
            stepHeadline: null,
            stepNumber: 0,
            dataToSend: {},
            prevSteps: [],
            formDataBuilder: {},
            formData: new FormData(),
            formName: form.action == (FormType.QUOTATION || FormType.QUOTATION_V3) ? 'Demande de devis' : 'Demande de rendez-vous',
            overlayDivDisplayed: false,
            loadingBtn: false,
            // Progress bar states
            progressBarWidth: form.action == (FormType.QUOTATION || FormType.QUOTATION_V3) ? '5%' : '33%',
            totalSteps: null,
            // States and functions for choice field
            handleChoiceFct: initChoiceComponent.handleChoiceFct,
            isMultipleChoiceState: initChoiceComponent.isMultipleChoiceState,
            choicesState: initChoiceComponent.choicesState,
            // States and functions for quantity field
            getQuantityStateFct: initQuantityComponent.getQuantityStateFct,
            handleQuantityFct: initQuantityComponent.handleQuantityFct,
            quantityActiveState: initQuantityComponent.quantityActiveState,
            smallActiveState: initQuantityComponent.smallActiveState,
            smallQuantityState: initQuantityComponent.smallQuantityState,
            mediumActiveState: initQuantityComponent.mediumActiveState,
            mediumQuantityState: initQuantityComponent.mediumQuantityState,
            largeActiveState: initQuantityComponent.largeActiveState,
            largeQuantityState: initQuantityComponent.largeQuantityState,
            loadingQuantityFct: initQuantityComponent.loadingQuantityFct,
            initQuantityFct: initQuantityComponent.initQuantityFct,
            // States and functions for meetHours field
            initBookingFormFct: initMeetHoursComponent.initBookingFormFct,
            dateFormationFct: initMeetHoursComponent.dateFormationFct,
            getAvailabilitiesFct: initMeetHoursComponent.getAvailabilitiesFct,
            handleClickSlotFct: initMeetHoursComponent.handleClickSlotFct,
            nextAvailabilitiesFct: initMeetHoursComponent.nextAvailabilitiesFct,
            previousAvailabilitiesFct: initMeetHoursComponent.previousAvailabilitiesFct,
            loadingMeethoursState: initMeetHoursComponent.loadingMeethoursState,
            slotStepState: initMeetHoursComponent.slotStepState,
            availabilitiesState: initMeetHoursComponent.availabilitiesState,
            shopPhoneState: initMeetHoursComponent.shopPhoneState,
            currentDateState: initMeetHoursComponent.currentDateState,
            meetHoursUrlState: initMeetHoursComponent.meetHoursUrlState,
            formattedDateRecapState: initMeetHoursComponent.formattedDateRecapState,
            hourChoicesState: initMeetHoursComponent.hourChoicesState,
            hasChoiceFct: initMeetHoursComponent.hasChoiceFct,
            shopCodeState: initMeetHoursComponent.shopCodeState,
            currentPageState: initMeetHoursComponent.currentPageState,
            limitState: initMeetHoursComponent.limitState,
            // Fields validation
            initInputFileFct: initFieldsComponent.initInputFileFct,
            validateLastStepFct: initFieldsComponent.validateLastStepFct,
            lengthLimitFct: initFieldsComponent.lengthLimitFct,
            maxCharsLastNameState: initFieldsComponent.maxCharsLastNameState,
            maxCharsFirstNameState: initFieldsComponent.maxCharsFirstNameState,
            maxCharsEmailState: initFieldsComponent.maxCharsEmailState,
            maxCharsAddressState: initFieldsComponent.maxCharsAddressState,
            maxCharsTextareaState: initFieldsComponent.maxCharsTextareaState,
            nbOfCharsState: initFieldsComponent.nbOfCharsState,
            submitDisabledState: initFieldsComponent.submitDisabledState,
            lastNameCheckValidState: initFieldsComponent.lastNameCheckValidState,
            firstNameCheckValidState: initFieldsComponent.firstNameCheckValidState,
            emailCheckValidState: initFieldsComponent.emailCheckValidState,
            phoneCheckValidState: initFieldsComponent.phoneCheckValidState,
            zipCheckValidState: initFieldsComponent.zipCheckValidState,
            cityCheckValidState: initFieldsComponent.cityCheckValidState,
            // Autocomplete
            validateInputFct: initFieldsComponent.validateInputFct,
            validateLengthFct: initFieldsComponent.validateLengthFct,
            openModalFct: initFieldsComponent.openModalFct,
            closeModalFct: initFieldsComponent.closeModalFct,
            displayResultsFct: initFieldsComponent.displayResultsFct,
            setCityInBasicFormFct: initFieldsComponent.setCityInBasicFormFct,
            results: initFieldsComponent.results,
            nbInputs: initFieldsComponent.nbInputs,
            displayResultsWording: initFieldsComponent.displayResultsWording,
            noResults: initFieldsComponent.noResults,
            tooShort: initFieldsComponent.tooShort,
            tooLong: initFieldsComponent.tooLong,
            modal: initFieldsComponent.modal,
            disabled: initFieldsComponent.disabled,
            modalState: false,
            productChoice: null,
            projectNumber: null,
            projectMaterial: null,
            projectTypeProgress: null,
            availability: null,
            projectRDV: null,
            ...{
                trybaPhoneMask,
            },
            init() {
                // if history has step, push in step var, else get init step
                if (history.state) {
                    const historyContent = JSON.parse(history.state);
                    this.step = historyContent[0];
                    this.prevSteps = historyContent[1];
                    this.stepNumber = historyContent[2];
                    this.stepTitle = this.step.title;
                    this.stepHeadline = this.step.headline;
                    this.dataToSend = historyContent[3];
                } else {
                    // Load init step
                    this.loadStep(this.initStepKey);
                }

                this.handleProgressSubStep();

                this.getProductUrlParams();

                this.completeFormWithWIndowInfo();
                // Display appropriated step group for progressbar
                this.formType = (form.action == FormType.QUOTATION || form.action == FormType.QUOTATION_V3) ? 'quotation' : 'appointment';
                this.loadFakeSteps(this.formType);

                if (this.formType === 'appointment') {
                    getPhonePrefixStore().enabled = false;
                }

                // Run send data layer function
                this.handleDataLayer('load', this.formName, this.stepNumber, this.step.tag);

                // Set base total nb of step in case of appointment form
                if (form.action == FormType.APPOINTMENT) {
                    this.totalSteps = 3;
                }

                const userData = getUserData();
                if (userData && userData.localisation) {
                    this.zipFromStorageValue = (userData.localisation.zip + ' ' + userData.localisation.city).trim();
                }

            },
            completeFormWithWIndowInfo() {
                const params = new URLSearchParams(document.location.search);
                const product = params.get('product');
                const quantity = params.get('quantity');
                const type = params.get('type');
                if (product && quantity && type) {
                    this.$refs.loaderSkipSteps.style.display = 'flex';
                    if (product == 'fenetres') {
                        setTimeout(() => {
                            this.getQuantityUrlParams();
                            // Set Timeout to wait 2 step
                            setTimeout(() => {
                                this.getTypeUrlParams();
                                setTimeout(() => {
                                    this.$refs.loaderSkipSteps.style.display = 'none';
                                }, 100);
                            }, 300);
                        }, 300);
                    }
                }
            },
            // Skip first step if product in url
            getProductUrlParams() {
                const params = new URLSearchParams(document.location.search);
                const product = params.get('product');
                if (product != null) {
                    if (product == 'fenetres' || product == 'portes-d-entree' || product == 'volets' || product == 'verandas' || product == 'portes-d-entrée' || product == 'volets' || product == 'pergolas' || product == 'stores-solaires') {
                        this.handleChoice(product);
                    }
                }
            },
            getQuantityUrlParams() {
                const params = new URLSearchParams(document.location.search);
                const quantity = params.get('quantity');
                if (quantity != null) {
                    this.handleChoice(quantity);
                }
            },
            getTypeUrlParams() {
                const params = new URLSearchParams(document.location.search);
                const type = params.get('type');
                if (type != null) {
                    this.handleChoice(type);
                }
            },
            fillFromLocalStorage() {
                if (this.zipFromStorageValue) {
                    // Play anim for label and fill input from local storage
                    (this.$refs.zipInput as HTMLInputElement).value = this.zipFromStorageValue;

                    this.$refs.zipInput.closest('.labelToTransformCustom')?.classList.add('floating-label');

                    setTimeout(() => {
                        this.verifyZipAfterFillByStorage();
                    }, 800);
                }
            },
            verifyZipAfterFillByStorage() {
                const zipInput = this.$refs.zipInput as HTMLInputElement | null;
                if (zipInput) {
                    storeLocatorAutocomplete(zipInput.value).then((cities) => {
                        (this.results as Array<City>) = cities;
                        this.validateLastStepFct('zip');
                        zipInput.blur();
                    });
                }
            },
            loadStep(stepKey: string) {
                this.step = this.formSteps[stepKey];
                this.stepTitle = this.step.title;
                this.stepHeadline = this.step.headline;
            },
            resetAllQuantity() {
                const quantities = this.step.quantities;
                quantities.forEach((quantity) => {
                    quantity.state = false;
                    quantity.state = 0;
                });
                this.quantityActiveState = false;
                this.smallQuantityState = 0;
                this.mediumQuantityState = 0;
                this.largeQuantityState = 0;
                this.smallActiveState = false;
                this.mediumActiveState = false;
                this.largeActiveState = false;
            },
            handleQuantityActiveStateFct(key: string) {
                const unknown: HTMLInputElement = this.$refs.quantityUnknown as HTMLInputElement;

                // At click, set quantity to 1 for the clicked quantity
                if (key == 'small' || key == 'medium' || key == 'large') {
                    if (unknown.checked) {
                        unknown.checked = false;
                    }
                }
                if (key == 'small') {
                    this.smallQuantityState = this.smallQuantityState == 0 ? this.smallQuantityState + 1 : this.smallQuantityState;
                    this.smallActiveState = !this.smallActiveState;
                } else if (key == 'medium') {
                    this.mediumQuantityState = this.mediumQuantityState == 0 ? this.mediumQuantityState + 1 : this.mediumQuantityState;
                    this.mediumActiveState = !this.mediumActiveState;
                } else if (key == 'large') {
                    this.largeQuantityState = this.largeQuantityState == 0 ? this.largeQuantityState + 1 : this.largeQuantityState;
                    this.largeActiveState = !this.largeActiveState;
                }
                // Check if at least one quantity is checked
                const quantities = this.step.quantities;
                let validStep = false;
                quantities.forEach((quantity) => {
                    if (quantity.state == true) {
                        validStep = true
                    }
                    return validStep;
                });
                // Check if at least one quantity or i dont knowis checked
                validStep || unknown.checked ? (this.quantityActiveState = true) : (this.quantityActiveState = false);
            },
            loadFakeSteps(formType: string) {
                this.fakeSteps =
                    formType == 'quotation'
                        ? [
                            {
                                number: 1,
                                name: '1. Produit',
                                acceptedSteps: ['product-type', 'which-material', 'how-many', 'garage-door-type', 'type-produit', 'sun-shade-type', 'window-type'],
                                status: 'inactive',
                            },
                            {
                                number: 2,
                                name: '2. Type de projet',
                                acceptedSteps: ['housing-type', 'project-type'],
                                status: 'inactive',
                            },
                            {
                                number: 3,
                                name: '3. Coordonnées',
                                acceptedSteps: ['coordinates'],
                                status: 'inactive',
                            },
                        ]
                        : [
                            {
                                number: 1,
                                name: '1. Disponibilité',
                                acceptedSteps: ['availability'],
                                status: 'inactive',
                            },
                            {
                                number: 2,
                                name: '2. Produit',
                                acceptedSteps: ['projectTypes'],
                                status: 'inactive',
                            },
                            {
                                number: 3,
                                name: '3. Coordonnées',
                                acceptedSteps: ['coordinates'],
                                status: 'inactive',
                            },
                        ];
            },
            // Choice function for checkbox/radio
            handleChoice(key: string, isMultiple: boolean, isPrevious = false, prevStep: any = null) {
                if (isPrevious && prevStep) {
                    let previousStepIndex: number | null = null;
                    this.prevSteps.map((item: any, index: number) => {
                        if (item.name == prevStep.name) {
                            previousStepIndex = index;
                        }
                        if (previousStepIndex && (index > previousStepIndex || previousStepIndex == 0)) {
                            this.prevSteps.splice(index);
                        }
                    });
                    if (!isMultiple) {
                        this.step = prevStep;
                        this.stepNumber = previousStepIndex;

                        this.handleChoiceFct(key, isMultiple);
                        this.handleDatasToSend(this.step.name, this.choicesState);

                        this.step.choices.map((item: any) => {
                            if (key == item.key) {
                                this.handleNextStep(item.nextStep);
                            }
                        });
                    } else {
                        this.handleChoiceFct(key, isMultiple);
                        this.handleDatasToSend(prevStep.name, this.choicesState);
                    }
                } else {
                    this.handleChoiceFct(key, isMultiple);

                    if (!isMultiple) {
                        // Add choices to dataToSend
                        this.handleDatasToSend(this.step.name, this.choicesState);

                        this.step.choices.map((item: any) => {
                            if (key == item.key) {
                                if (item.data.steps) {
                                    this.totalSteps = item.data.steps;
                                }
                                this.handleNextStep(item.nextStep);
                            }
                        });
                    } else {
                        this.handleDatasToSend(this.step.name, this.choicesState);
                    }
                }
                this.handleProgressSubStep();
            },

            handleProgressSubStep() {

                const correctWording = {
                    'fenetres' : 'Fenêtres et portes-fenêtres',
                    'portes-d-entree' : 'Portes d’entrée',
                    'volets' : 'Volets',
                    'verandas' : 'Vérandas',
                    'pergolas' : 'Pergolas',
                    'stores-solaires' : 'Stores, portes de garage, portails',
                    'pvc' : 'PVC',
                    'aluminium' : 'Aluminium',
                    'bois' : 'Bois',
                    'je-ne-sais-pas' : 'Je ne sais pas',
                }
                console.log(this.dataToSend['which-material'])
                // Step for Quotation
                if (this.dataToSend['product-type']) this.productChoice = correctWording[this.dataToSend['product-type'][0]]; else this.productChoice = null;
                if (this.dataToSend['how-many']) this.projectNumber = this.dataToSend['how-many']; else this.projectNumber = null;
                if (this.dataToSend['which-material']) this.projectMaterial = correctWording[this.dataToSend['which-material']]; else this.projectMaterial = null;
                if (this.dataToSend['project-type']) this.projectTypeProgress = this.dataToSend['project-type']; else this.projectTypeProgress = null;

                // Step for Appointment
                if (this.dataToSend['availability']) {
                    const date = this.dataToSend['availability'][0];
                    this.availability = this.translateDateToFrench(date);
                } else {
                    this.availability = null;
                }

                if (this.dataToSend['projectTypes'])
                    this.projectRDV = this.dataToSend['projectTypes'].map((item: any) => {
                        return ` ${correctWording[item]}`;
                    });
                else
                    this.projectRDV = null;

            },
           translateDateToFrench(string: string) {
                    if (typeof string !== 'string') {
                        throw new Error('Expected a string like "2025-11-07 morning"');
                    }
                    const [dateString, timeOfDay] = string.split(' ');

                    const date = new Date(dateString);
                    // if (isNaN(date)) throw new Error('Invalid date format');

                    // Format the date in French locale
                    const formattedDate = date.toLocaleDateString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                    });

                    const timeMap = {
                        morning: 'matin',
                        afternoon: 'après-midi'
                    };

                    const frenchTime = timeMap[timeOfDay?.toLowerCase()] || timeOfDay;

                    return `${formattedDate} ${frenchTime}`;

            },
            // Datas traitment for quantity field
            handleQuantity(isPrevious = false, prevStep: any = null) {
                const combinedQuantity = {};
                if (this.smallActiveState) {
                    combinedQuantity['small'] = this.smallQuantityState;
                }
                if (this.mediumActiveState) {
                    combinedQuantity['medium'] = this.mediumQuantityState;
                }
                if (this.largeActiveState) {
                    combinedQuantity['large'] = this.largeQuantityState;
                }
                this.handleDatasToSend('how-many', [combinedQuantity]);

                if (isPrevious && prevStep) {
                    this.step = prevStep;

                    let previousStepIndex: number | null = null;
                    this.prevSteps.map((item: any, index: number) => {
                        if (item.name == prevStep.name) {
                            previousStepIndex = index;
                            this.stepNumber = previousStepIndex;
                            this.prevSteps.splice(index);
                        }
                    });

                    setTimeout(() => {
                        this.handleNextStep(prevStep.fieldsNextStep);
                    }, 150);
                } else {
                    this.handleNextStep(this.step.fieldsNextStep);
                }
            },
            // Datas traitment for meetHours field
            handleSlot(slot: AvailibilitySlot, item: AvailibilitySlot, isPrevious = false, prevStep: any = null) {
                if (isPrevious && prevStep) {
                    let previousStepIndex: number | null = null;
                    this.prevSteps.map((item: any, index: number) => {
                        if (item.name == prevStep.name) {
                            previousStepIndex = index;
                        }
                        if (previousStepIndex && (index > previousStepIndex || previousStepIndex == 0)) {
                            this.prevSteps.splice(index);
                        }
                    });

                    this.step = prevStep;
                    this.stepNumber = previousStepIndex;

                    this.handleClickSlotFct(slot, item);
                    this.handleDatasToSend('availability', [slot.value]);
                    this.handleNextStep(this.step.fieldsNextStep);
                } else {
                    this.handleClickSlotFct(slot, item);
                    this.handleDatasToSend('availability', [slot.value]);
                    this.handleNextStep(this.step.fieldsNextStep);
                }
                this.handleProgressSubStep();
            },
            // Add datas in array to send
            handleDatasToSend(key: string, datas: Array<string>) {
                this.dataToSend[key] = [...datas];
            },
            // Prev/Next step fct
            handleNextStep(nextStepKey: string) {
                this.overlayDivDisplayed = this.$store.global.isMobile ? false : true;

                setTimeout(
                    () => {
                        // Push in array the step
                        this.prevSteps.push(this.step);

                        // Increase step number
                        this.stepNumber = this.stepNumber + 1;
                        window.location.hash = 'etape' + this.stepNumber;

                        // Handle progress bar Fct
                        this.handleProgress('next');

                        // Construct dataLayer for appointment (custom ^^')
                        if (form.action == FormType.APPOINTMENT) {
                            this.handleDataLayer('next', this.formName);
                        }

                        this.loadStep(nextStepKey);

                        // Construct dataLayer for quotation (custom ^^')
                        if (form.action == FormType.QUOTATION) {
                            this.handleDataLayer('next', this.formName);
                        }

                        // Scroll to step in mobile
                        if (this.$store.global.isMobile && this.$refs.tunnelForm) {
                            this.$nextTick(() => {
                                window.scrollTo({
                                    top: this.$refs.tunnelForm.offsetTop + 60,
                                    left: 0,
                                    behavior: 'smooth',
                                });
                                document.addEventListener(
                                    'scrollend',
                                    () => {
                                        document.documentElement.scrollTop = this.$refs.tunnelForm.offsetTop + 60 + 0.1;
                                        document.body.scrollTop = this.$refs.tunnelForm.offsetTop + 60 + 0.1;
                                    },
                                    { once: true },
                                );
                            });
                        }

                        this.makeHistory();

                        this.overlayDivDisplayed = false;
                    },
                    this.$store.global.isMobile ? 0 : 200,
                );
            },
            handlePreviousStep() {
                this.overlayDivDisplayed = this.$store.global.isMobile ? false : true;

                setTimeout(
                    () => {
                        // Delete step datas in dataToSend state and reset states used in component
                        if (this.dataToSend[this.step.name]) {
                            if (this.step.name == 'how-many') {
                                this.quantityActiveState = false;

                                this.smallActiveState = false;
                                this.mediumActiveState = false;
                                this.largeActiveState = false;
                            }
                            if (this.step.name == 'projectTypes' && this.step.canSelectMultipleChoices) {
                                this.choicesState = [];
                            }
                            delete this.dataToSend[this.step.name];
                        }

                        // If step is coordinates, reset states of checkmark icons
                        if (this.step.name == 'coordinates') {
                            this.phoneCheckValidState = undefined;
                            this.lastNameCheckValidState = undefined;
                            this.firstNameCheckValidState = undefined;
                            this.emailCheckValidState = undefined;
                            this.zipCheckValidState = undefined;
                            this.cityCheckValidState = undefined;
                        }

                        this.step = this.prevSteps.pop();
                        this.stepNumber = this.stepNumber - 1;
                        this.stepTitle = this.step.title;

                        // Handle progress bar Fct
                        this.handleProgress('previous');

                        // Scroll to step in mobile
                        if (this.$store.global.isMobile && this.$refs.tunnelForm) {
                            this.$nextTick(() => {
                                window.scrollTo({
                                    top: this.$refs.tunnelForm.offsetTop - 60 + 0.1,
                                    left: 0,
                                    behavior: 'smooth',
                                });
                                document.addEventListener(
                                    'scrollend',
                                    () => {
                                        document.documentElement.scrollTop = this.$refs.tunnelForm.offsetTop - 60 + 0.1;
                                        document.body.scrollTop = this.$refs.tunnelForm.offsetTop - 60 + 0.1;
                                    },
                                    { once: true },
                                );
                            });
                        }

                        if (this.stepNumber == 0) {
                            if (form.action == FormType.APPOINTMENT && this.$store.global.isMobile) {
                                this.progressBarWidth = '33%';
                            } else {
                                this.progressBarWidth = '5%';
                            }
                            window.location.hash = '';
                        } else {
                            window.location.hash = 'etape' + this.stepNumber;
                        }
                        this.makeHistory();

                        // Construct dataLayer (custom ^^')
                        this.handleDataLayer('previous', this.formName, this.stepNumber);

                        this.overlayDivDisplayed = false;
                    },
                    this.$store.global.isMobile ? 0 : 200,
                );
            },
            // Handle history
            makeHistory() {
                this.historyContent = [];
                this.historyContent.push(this.step);
                this.historyContent.push(this.prevSteps);
                this.historyContent.push(this.stepNumber);
                this.historyContent.push(this.dataToSend);
                this.historyContent = JSON.stringify(this.historyContent);

                history.pushState(this.historyContent, 'etape', window.location.hash);
            },
            // Progress bar fct
            handleProgress(type: string) {
                if (type == 'next') {
                    this.fakeSteps.map((fakeStep: any) => {
                        fakeStep.acceptedSteps.map((fakeStepSteps: any) => {
                            if (this.step.name == fakeStepSteps) {
                                fakeStep.status = 'active';
                            }
                        });
                    });
                } else if (type == 'previous') {
                    let previousIndex: any = null;

                    this.fakeSteps.map((fakeStep: any, index: number) => {
                        fakeStep.acceptedSteps.map((fakeStepSteps: any) => {
                            if (this.step.name === fakeStepSteps) {
                                previousIndex = index;
                            } else {
                                if (previousIndex < index) {
                                    this.fakeSteps[index].status = 'inactive';
                                }
                            }
                        });
                    });
                }

                this.progressBarWidth = `${((this.stepNumber + 1) / this.totalSteps) * 100}%`;

                return;
            },
            handleProgressBarClick(fakeStep: any) {
                for (const [key, formStep] of Object.entries(this.formSteps)) {
                    if ((formStep as any).name == fakeStep.acceptedSteps[0]) {
                        if (key != 'initB') {
                            let previousStepIndex: number | null = null;
                            this.prevSteps.map((item: any, index: number) => {
                                const translatedKey = key == 'init' ? 'product-type' : key;
                                if (translatedKey == item.name) {
                                    previousStepIndex = index;
                                }
                                if (previousStepIndex && this.prevSteps[previousStepIndex]) {
                                    if (this.prevSteps[previousStepIndex].name == 'project-type' || this.prevSteps[previousStepIndex].name == 'projectTypes') {
                                        this.step = formStep;
                                        this.stepNumber = previousStepIndex;
                                    } else {
                                        if (index > previousStepIndex || previousStepIndex == 0) {
                                            this.prevSteps.splice(index);
                                            this.step = formStep;
                                            this.stepNumber = previousStepIndex;
                                        }
                                    }
                                }
                            });
                        }
                    }
                }
            },
            // Build formData and submit form
            async createFormDatas(): Promise<void> {
                if (this.loadingBtn) {
                    return;
                }
                // Make loading btn true
                this.loadingBtn = true;
                // Reset formData
                let error = null;
                this.formData = new FormData();
                // Empty the formData builder state
                this.formDataBuilder = {};
                // Add form datas (all others steps before the last)
                for (const item in this.dataToSend) {
                    if (this.dataToSend[item][0] instanceof Object) {
                        this.formDataBuilder[item] = JSON.stringify(this.dataToSend[item][0]);
                    } else {
                        this.formDataBuilder[item] = this.dataToSend[item];
                    }
                }

                // Add last step datas
                const lastStepInputs = document.querySelector('.lastStepFields')?.querySelectorAll('input');
                const contactMethodsArray: Array<string> = [];
                let zipCity = '';

                (lastStepInputs || []).forEach((input: HTMLInputElement) => {
                    if (input.type == 'radio' && input.checked) {
                        this.formDataBuilder[input.name] = input.value;
                    }
                    if (input.type == 'checkbox' && input.name != 'contactMethods' && input.checked) {
                        if (input.name != 'email' && input.name != 'sms' && input.name != 'phone') {
                            this.formDataBuilder[input.name] = input.value;
                        }
                    }
                    if (input.type == 'checkbox' && input.name == 'contactMethods') {
                        if (input.checked) {
                            this.formDataBuilder['allowContact'] = true;
                        } else {
                            this.formDataBuilder['allowContact'] = false;
                        }
                    }
                    if (this.formDataBuilder['allowContact']) {
                        if (input.type == 'checkbox' && input.name == 'email' && input.checked) {
                            contactMethodsArray.push(input.value);
                        }
                        if (input.type == 'checkbox' && input.name == 'sms' && input.checked) {
                            contactMethodsArray.push(input.value);
                        }
                        if (input.type == 'checkbox' && input.name == 'phone' && input.checked) {
                            contactMethodsArray.push(input.value);
                        }
                    }
                    if (input.type == 'text' && input.value != '') {
                        if (input.name == 'zip-code[zip]') {
                            zipCity = input.value;
                        } else {
                            this.formDataBuilder[input.name] = input.value;
                        }
                    }
                    if (input.type == 'tel' && input.value != '') {
                        const phonePrefixStore = getPhonePrefixStore();
                        this.formDataBuilder[input.name] = phonePrefixStore.enabled
                            ? formatPhoneForCrm(input.value, phonePrefixStore.country)
                            : input.value;
                    }
                    if (input.type == 'file') {
                        Array.from(input.files as FileList).forEach((file: File) => {
                            this.formDataBuilder['images[]'] = file;
                        });
                    }
                    if (input.type == 'email' && input.value != '') {
                        this.formDataBuilder[input.name] = input.value;
                    }
                });
                this.formDataBuilder['contactMethods[]'] = contactMethodsArray;
                if (zipCity) {
                    this.formDataBuilder['location'] = zipCity.trim();
                }

                // Add textarea to last step
                const lastStepTextarea = document.querySelector('.lastStepFields')?.querySelector('textarea');
                if (lastStepTextarea && lastStepTextarea.value != '') {
                    this.formDataBuilder[lastStepTextarea.name] = lastStepTextarea.value;
                }

                // Remove contact by bird (like a captcha) from formDatas
                this.formDataBuilder['contact_by_bird'] = '';

                // Fill formData
                for (const [key, value] of Object.entries(this.formDataBuilder)) {
                    this.formData.append(key, value);
                }

                addUtmToFormData(this.formData);
                addQwamplifyToFormData(this.formData);

                // Send datas to dataLayer
                this.handleDataLayer('next', this.formName);
                this.handleDataLayer('submit', this.formName);

                // Send form
                try {
                    const formSent = await send(this.formData, form.action);
                    window.location.href = (formSent as ApiSuccess).confirmation;
                    // @ts-ignore
                } catch (e: ApiError) {
                    // console.log(e)
                    this.confirmation = false;
                    this.formError(e);
                    error = e;
                } finally {
                    if (error) {
                        this.loadingBtn = false;
                        this.disabled = false;
                    } else {
                        this.loadingBtn = true;
                        this.disabled = true;
                    }
                }
            },
            formError(errors: ApiError): void {
                this.errorMessage = '';
                const lastStepFields = document.querySelector('.lastStepFields');
                if (lastStepFields) {
                    this.allInputs = lastStepFields.querySelectorAll('.quotationInput');
                } else {
                    this.allInputs = [];
                }

                this.allInputs.forEach((item: HTMLInputElement) => {
                    const span = (item.parentNode?.parentNode as HTMLDivElement).getElementsByTagName('span');
                    if (span.length) {
                        span[0].textContent = '';
                    }
                    Object.keys(errors).forEach((key) => {
                        if (item.name === key) {
                            if (key == 'phone') {
                                this.phoneCheckValidState = false;
                                this.telValid = false;
                            }
                            if (key == 'lastName') {
                                this.lastNameCheckValidState = false;
                                this.lastNameValid = false;
                            }
                            if (key == 'firstName') {
                                this.firstNameCheckValidState = false;
                                this.firstNameValid = false;
                            }
                            if (key == 'email') {
                                this.emailCheckValidState = false;
                                this.emailValid = false;
                            }
                            if (span.length) {
                                span[0].textContent = errors[key][0];
                            }
                        }
                    });
                });
            },
            // Custom DataLayer
            handleDataLayer(type: string, formName: string, step?: number | string | null) {
                const actionName = 'productCheckout';
                const categoryName = 'tunnel';

                if (formName == 'Demande de devis') {
                    if (type == 'load') {
                        sendTunnelForm({
                            slot1: 'devis-en-ligne',
                            slot2: 'etape' + '1',
                            slot3: 'choix-categorie',
                        }, { categoryName: categoryName }, { actionName: actionName });
                    }

                    if (type == 'submit') {
                        sendEventDataLayer('click', 'tunnel-devis', 'consentement', this.formDataBuilder['contactMethods[]'].length == 0 ? 'aucun' : this.formDataBuilder['contactMethods[]'].length == 1 ? this.formDataBuilder['contactMethods[]'] : 'deux', false, 0, {
                            slot1: 'devis-en-ligne',
                            slot2: 'etape' + '3',
                            slot3: 'coordonnees',
                        });

                        sendEventDataLayer('click', 'tunnel-devis', 'detail-projet', this.formDataBuilder['project-description'] ? 'oui' : 'non', false, 0, {
                            slot1: 'devis-en-ligne',
                            slot2: 'etape' + '3',
                            slot3: 'coordonnees',
                        });

                        sendTunnelForm(
                            { slot1: 'devis-en-ligne', slot2: 'confirmation' },
                            { categoryName: categoryName },
                            { actionName: 'productPurchase' },
                            {
                                id: this.dataToSend['product-type'][0],
                                category: this.dataToSend['product-type'][0],
                                name: this.dataToSend['product-type'][0],
                            },
                            true,
                        );
                    }

                    if (type == 'next') {
                        const productType = JSON.stringify(this.dataToSend['product-type'].toString());
                        sessionStorage.setItem('productPurchase', JSON.stringify(this.dataToSend['product-type']));
                        setTimeout(() => {
                            //  console.log(this.prevSteps)
                            // Event avec décalage
                            // if(this.dataToSend['product-type'] && (this.prevSteps[this.stepNumber - 1].name == 'product-type')) {
                            //     sendEventDataLayer('click', 'tunnel-devis', 'gammes', this.dataToSend['product-type'][0], false, 0, { slot1: "devis-en-ligne", slot2: "etape" + "1", slot3: 'choix-categorie' })
                            //     sessionStorage.setItem("productPurchase", JSON.stringify(this.dataToSend['product-type']));
                            // }
                            // if(this.dataToSend['shutter-type'] && (this.prevSteps[this.stepNumber - 1].name == 'shutter-type')) {
                            //     sendEventDataLayer('click', 'tunnel-devis', 'choix-produit', this.dataToSend['shutter-type'][0], false, 0, { slot1: "devis-en-ligne", slot2: "etape" + "1-2", slot3: 'choix-produit' })
                            // }
                            // if(this.dataToSend['sun-shade-type'] && (this.prevSteps[this.stepNumber - 1].name == 'sun-shade-type')) {
                            //     sendEventDataLayer('click', 'tunnel-devis', 'choix-produit', this.dataToSend['sun-shade-type'][0], false, 0, { slot1: "devis-en-ligne", slot2: "etape" + "1-1", slot3: 'choix-produit' })
                            // }
                            // if(this.dataToSend['how-many'] && (this.prevSteps[this.stepNumber - 1].name == 'how-many')) {
                            //     sendEventDataLayer('click', 'tunnel-devis', 'nombre-produits', this.dataToSend['how-many'][0], false, 0, { slot1: "devis-en-ligne", slot2: "etape" + "1-1", slot3: 'nombre-produits' })
                            // }
                            // if(this.dataToSend['which-material'] && (this.prevSteps[this.stepNumber - 1].name == 'which-material')) {
                            //     let productType = JSON.stringify(this.dataToSend['product-type'].toString());
                            //     if (productType == '"portes-d-entree"') {
                            //         console.log(this.prevSteps[this.stepNumber - 1].name)
                            //         sendEventDataLayer('click', 'tunnel-devis', 'materiau-produit', this.dataToSend['which-material'][0], false, 0, { slot1: "devis-en-ligne", slot2: "etape" + "1-1", slot3: 'materiau-produit' })
                            //     } else {
                            //         sendEventDataLayer('click', 'tunnel-devis', 'materiau-produit', this.dataToSend['which-material'][0], false, 0, { slot1: "devis-en-ligne", slot2: "etape" + "1-2", slot3: 'materiau-produit' })
                            //     }
                            // }
                            // if(this.dataToSend['project-type'] && (this.prevSteps[this.stepNumber - 1].name == 'project-type')) {
                            //     sendEventDataLayer('click', 'tunnel-devis', 'type-de-projet', this.dataToSend['project-type'][0], false, 0, { slot1: "devis-en-ligne", slot2: "etape" + "2", slot3: 'type-de-projet' })
                            // }

                            // Event sans décalage pour chaque type de produit
                            if (productType == '"fenetres"') {
                                if (this.dataToSend['product-type'] && this.prevSteps[this.stepNumber - 1].name == 'product-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'gammes', this.dataToSend['product-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '1-1',
                                        slot3: 'nombre-produits',
                                    });
                                }
                                if (this.dataToSend['how-many'] && this.prevSteps[this.stepNumber - 1].name == 'how-many') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'nombre-produits', JSON.stringify(this.dataToSend['how-many'][0]), false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '1-2',
                                        slot3: 'materiau-produit',
                                    });
                                }
                                if (this.dataToSend['which-material'] && this.prevSteps[this.stepNumber - 1].name == 'which-material') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'materiau-produit', this.dataToSend['which-material'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '2',
                                        slot3: 'type-de-projet',
                                    });
                                }
                                if (this.dataToSend['project-type'] && this.prevSteps[this.stepNumber - 1].name == 'project-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'type-de-projet', this.dataToSend['project-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '3',
                                        slot3: 'coordonnees',
                                    });
                                }
                            } else if (productType == '"portes-d-entree"') {
                                if (this.dataToSend['product-type'] && this.prevSteps[this.stepNumber - 1].name == 'product-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'gammes', this.dataToSend['product-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '1-1',
                                        slot3: 'materiau-produit',
                                    });
                                }
                                if (this.dataToSend['which-material'] && this.prevSteps[this.stepNumber - 1].name == 'which-material') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'materiau-produit', this.dataToSend['which-material'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '2',
                                        slot3: 'type-de-projet',
                                    });
                                }
                                if (this.dataToSend['project-type'] && this.prevSteps[this.stepNumber - 1].name == 'project-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'type-de-projet', this.dataToSend['project-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '3',
                                        slot3: 'coordonnees',
                                    });
                                }
                            } else if (productType == '"volets"') {
                                if (this.dataToSend['product-type'] && this.prevSteps[this.stepNumber - 1].name == 'product-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'gammes', this.dataToSend['product-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '1-1',
                                        slot3: 'nombre-produits',
                                    });
                                }
                                if (this.dataToSend['how-many'] && this.prevSteps[this.stepNumber - 1].name == 'how-many') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'nombre-produits', this.dataToSend['how-many'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '1-2',
                                        slot3: 'choix-produit',
                                    });
                                }
                                if (this.dataToSend['shutter-type'] && this.prevSteps[this.stepNumber - 1].name == 'shutter-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'choix-produit', this.dataToSend['shutter-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '2',
                                        slot3: 'type-de-projet',
                                    });
                                }
                                if (this.dataToSend['project-type'] && this.prevSteps[this.stepNumber - 1].name == 'project-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'type-de-projet', this.dataToSend['project-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '3',
                                        slot3: 'coordonnees',
                                    });
                                }
                            } else if (productType == '"stores-solaires"') {
                                if (this.dataToSend['product-type'] && this.prevSteps[this.stepNumber - 1].name == 'product-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'gammes', this.dataToSend['product-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '1-1',
                                        slot3: 'choix-produit',
                                    });
                                }
                                if (this.dataToSend['sun-shade-type'] && this.prevSteps[this.stepNumber - 1].name == 'sun-shade-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'choix-produit', this.dataToSend['sun-shade-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '2',
                                        slot3: 'type-de-projet',
                                    });
                                }
                                if (this.dataToSend['project-type'] && this.prevSteps[this.stepNumber - 1].name == 'project-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'type-de-projet', this.dataToSend['project-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '3',
                                        slot3: 'coordonnees',
                                    });
                                }
                            }
                            // Pour Pergolas vérandas
                            else {
                                if (this.dataToSend['product-type'] && this.prevSteps[this.stepNumber - 1].name == 'product-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'gammes', this.dataToSend['product-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '2',
                                        slot3: 'type-de-projet',
                                    });
                                }
                                if (this.dataToSend['project-type'] && this.prevSteps[this.stepNumber - 1].name == 'project-type') {
                                    sendEventDataLayer('click', 'tunnel-devis', 'type-de-projet', this.dataToSend['project-type'][0], false, 0, {
                                        slot1: 'devis-en-ligne',
                                        slot2: 'etape' + '3',
                                        slot3: 'coordonnees',
                                    });
                                }
                            }
                        }, 150);
                    }

                    if (type == 'previous') {
                        sendEventDataLayer('click', 'tunnel-devis', 'etape-precedente', step);
                    }
                }

                if (this.formName == 'Demande de rendez-vous') {
                    if (type == 'load') {
                        sendTunnelForm({
                            slot1: 'demande-de-rendez-vous',
                            slot2: 'etape' + '1',
                            slot3: 'date',
                        }, { categoryName: categoryName }, { actionName: actionName });
                    }

                    if (type == 'submit') {
                        sendTunnelForm(
                            { slot1: 'demande-de-rendez-vous', slot2: 'confirmation' },
                            { categoryName: categoryName },
                            { actionName: 'productPurchase' },
                            {
                                productCheckout: this.dataToSend['projectTypes'],
                            },
                            true,
                        );
                    }

                    if (type == 'next') {
                        if (this.step.name == 'availability') {
                            sendEventDataLayer('click', 'tunnel-rdv', 'produit', this.dataToSend['project-type'], false, 0, {
                                slot1: 'demande-de-rendez-vous',
                                slot2: 'etape' + '2',
                                slot3: 'produit',
                            });
                        }
                        if (this.step.name == 'projectTypes') {
                            sendEventDataLayer('click', 'tunnel-rdv', 'produit', this.dataToSend['project-type'], false, 0, {
                                slot1: 'demande-de-rendez-vous',
                                slot2: 'etape' + '3',
                                slot3: 'coordonnees',
                            });
                        }
                    }

                    if (type == 'previous') {
                        sendEventDataLayer('click', 'tunnel-rdv', 'etape-precedente', step);
                    }
                }
            },
            autocompleteModale(selectedItem: any, domItem: any, index = 0) {
                this.setCityInBasicFormFct(selectedItem, domItem, index);
                this.modalState = false;
            },
            selectedElement(item: any, index = 0) {
                //Store gps position
                this.selectedItem = item;
                setUserDataLocalisation({ zip: this.selectedItem.codesPostaux[index], city: this.selectedItem.nom });
                setUserDataShop(this.selectedItem.magasin.telephone, this.selectedItem.magasin.cp, this.selectedItem.nom, this.selectedItem.magasin.adresse, this.selectedItem.magasin.nom, this.selectedItem.magasin.code, this.selectedItem.magasin.url ? this.selectedItem.magasin.url : `${window.location.origin}/espace-conseil/?shop=${this.selectedItem.magasin.code}`, this.selectedItem.magasin.latitude, this.selectedItem.magasin.longitude);
            },
            autocompleteFromFieldName: autocompleteFromFieldName,
        };
    })
})
